import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Collapse, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  loanTrackingEventTypeDisplay,
  loanTrackingEventStatusDisplay,
} from 'components/global/Constant/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { caretDownIcon, caretRightIcon } from 'components/global/Constant/Icons';
import api from 'api/api';
import {
  isEdoAdminOrOwner,
  canSeeEdoLoanBatchManagementL1Queue,
  canSeeEdoLoanBatchManagementL2Queue,
  canResetEntireLoanBatch,
  canUploadEdoLoanBatchStepAttachments,
  canDeleteEdoLoanBatchStepAttachment,
  canUploadEdoLoanBatchFundingMemo,
} from 'libs/edoPermissions';
import {
  fetchLoanProcessingBatches,
  approveBatchL1,
  approveBatchL2,
  approveBatchOutgoingReceipt,
  approveBatchTrusteeSend,
  uploadBatchFundingMemo,
  uploadBatchStepDocument,
  deleteBatchStepDocument,
  fetchBatchStepDocumentPreviewBase64,
  fetchBatchFundingMemoPreviewBase64,
  fetchBatchSigningSheetBase64,
  fetchBatchInitialsLogBase64,
  openLoanBatchStepDocumentInNewTab,
  withdrawApplicationFromBatch,
  resetLoanProcessingBatch,
} from 'libs/loanProcessingBatchApi';
import {
  notifyApplicationLoanTrackingRefresh,
  notifyLoanProcessingBatchesDashboardDirty,
  LOAN_PROCESSING_BATCHES_DASHBOARD_DIRTY_EVENT,
} from 'libs/loanProcessingBatchesDashboardDirty';
import Loader from 'components/global/Loader';
import dateOptions from 'components/global/Constant/dateOptions';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import DashboardTablePagination from './DashboardTablePagination';
import LoanBatchRemovalNoteModal from 'components/views/Edo/LoanBatchRemovalNoteModal';

const SECTION_COLLAPSE_MS = 400;

/** Dashboard queue cards: server-paged (full list is on Batch Processing). */
const DASHBOARD_BATCH_PAGE_SIZE = 5;

const STATUS_LABELS = {
  DRAFT: 'Draft',
  PENDING_L1: 'Pending Management (L1)',
  PENDING_L2: 'Pending Management (L2)',
  PENDING_OUTGOING_RECEIPT: 'Outgoing — receipt',
  PENDING_TRUSTEE: 'Outgoing — trustee send',
  COMPLETED: 'Completed',
};

const BATCH_STEP_UPLOAD_LABEL = {
  management_l1: 'Upload Attachment (L1)',
  management_l2: 'Upload Attachment (L2)',
  outgoing: 'Upload Attachment (Outgoing)',
};

const BATCH_STEP_DISPLAY_LABEL = {
  management_l1: 'Management L1',
  management_l2: 'Management L2',
  outgoing: 'Outgoing',
};

function batchAttachmentStepFromStatus(status) {
  if (status === 'PENDING_L1') return 'management_l1';
  if (status === 'PENDING_L2') return 'management_l2';
  if (status === 'PENDING_OUTGOING_RECEIPT' || status === 'PENDING_TRUSTEE') return 'outgoing';
  return null;
}

function formatLastEventType(code, status) {
  if (!code) return '—';
  return loanTrackingEventTypeDisplay(code, status);
}

function formatLastEventStatusCol(code, status) {
  if (!status) return '—';
  return loanTrackingEventStatusDisplay(code, status);
}

function formatSubmittedForProcessing(iso) {
  if (iso == null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', dateOptions.dateTimeFormatted);
}

function createObjectUrlFromBase64(dataBase64, fileType = 'application/pdf') {
  if (!dataBase64) return null;
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: fileType || 'application/pdf' });
  return URL.createObjectURL(blob);
}

const PDF_PREVIEW_HEADING = {
  memo: 'Funding Memo',
  signing: 'Initial / Signing Sheet',
  initials: 'Combined Loan Tracking (Initials Log)',
  attachment: 'Batch Attachment',
};

const PDF_PREVIEW_ERROR = {
  memo: 'Could not load Funding Memo preview.',
  signing: 'Could not load Initial / Signing Sheet preview.',
  initials: 'Could not load Combined Loan Tracking (Initials Log) preview.',
};

function BatchRow({ batch, onChanged, canL1, canL2Outgoing, canRemove, userData, defaultOpen, isLastInSection }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [busy, setBusy] = useState(false);
  const [removalModal, setRemovalModal] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewKind, setPdfPreviewKind] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const pdfPreviewBlobUrlRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const allowMutateBatchMembers = canRemove && batch.status !== 'COMPLETED';
  const currentAttachmentStep = batchAttachmentStepFromStatus(batch.status);
  const canUploadCurrentStepAttachment =
    Boolean(currentAttachmentStep) && canUploadEdoLoanBatchStepAttachments(userData, batch);
  const canDeleteCurrentStepAttachment = () =>
    canDeleteEdoLoanBatchStepAttachment(userData, batch);
  const allStepDocuments = Array.isArray(batch?.documents) ? batch.documents : [];
  const showAttachmentsPanel = Boolean(currentAttachmentStep) || batch.status === 'COMPLETED';
  const showUploadAttachmentButton = Boolean(
    currentAttachmentStep && canUploadCurrentStepAttachment
  );

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      const memberIds = (batch.members || []).map((m) => m.applicationId).filter(Boolean);
      if (memberIds.length > 0) {
        notifyApplicationLoanTrackingRefresh({ applicationIds: memberIds });
      }
      await onChanged();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'Action failed');
    } finally {
      if (isMountedRef.current) setBusy(false);
    }
  };

  const onMemoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const result = reader.result;
      if (typeof result !== 'string') return;
      const base64 = result.split(',')[1] || result;
      if (isMountedRef.current) setFileUploading(true);
      try {
        await uploadBatchFundingMemo(batch.id, base64, file.name);
        await onChanged();
      } catch (err) {
        window.alert(err?.message || 'Upload failed');
      } finally {
        if (isMountedRef.current) setFileUploading(false);
        e.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const onStepAttachmentFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentAttachmentStep) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const result = reader.result;
      if (typeof result !== 'string') return;
      const base64 = result.split(',')[1] || result;
      if (isMountedRef.current) setFileUploading(true);
      try {
        await uploadBatchStepDocument(batch.id, {
          step: currentAttachmentStep,
          fileBase64: base64,
          fileName: file.name,
          fileType: file.type,
        });
        notifyApplicationLoanTrackingRefresh({
          applicationIds: (batch.members || []).map((m) => m.applicationId).filter(Boolean),
        });
        await onChanged();
      } catch (err) {
        window.alert(err?.message || 'Upload failed');
      } finally {
        if (isMountedRef.current) setFileUploading(false);
        e.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const onDeleteStepAttachment = async (documentId) => {
    setBusy(true);
    try {
      await deleteBatchStepDocument(batch.id, documentId);
      notifyApplicationLoanTrackingRefresh({
        applicationIds: (batch.members || []).map((m) => m.applicationId).filter(Boolean),
      });
      await onChanged();
    } catch (err) {
      window.alert(err?.message || 'Delete failed');
    } finally {
      if (isMountedRef.current) setBusy(false);
    }
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewLoading(false);
  };

  const handlePdfPreviewExited = () => {
    if (pdfPreviewBlobUrlRef.current) {
      URL.revokeObjectURL(pdfPreviewBlobUrlRef.current);
      pdfPreviewBlobUrlRef.current = null;
    }
    setPdfPreviewKind(null);
    setPdfPreviewTitle('');
    setPdfPreviewUrl(null);
    setPdfPreviewLoading(false);
  };

  const openPdfPreview = (kind) => {
    setPdfPreviewKind(kind);
    setPdfPreviewOpen(true);
    setPdfPreviewTitle('');
    setPdfPreviewUrl(null);
    setPdfPreviewLoading(true);
    void (async () => {
      try {
        let raw;
        if (kind === 'memo') {
          raw = await fetchBatchFundingMemoPreviewBase64(batch.id);
        } else if (kind === 'signing') {
          raw = await fetchBatchSigningSheetBase64(batch.id);
        } else {
          raw = await fetchBatchInitialsLogBase64(batch.id);
        }
        const b64 = typeof raw === 'string' ? raw : raw?.data;
        if (!b64 || typeof b64 !== 'string') {
          throw new Error('Empty preview payload');
        }
        const objectUrl = createObjectUrlFromBase64(b64, 'application/pdf');
        if (!objectUrl) {
          throw new Error('Could not build preview');
        }
        if (pdfPreviewBlobUrlRef.current) {
          URL.revokeObjectURL(pdfPreviewBlobUrlRef.current);
        }
        pdfPreviewBlobUrlRef.current = objectUrl;
        if (isMountedRef.current) setPdfPreviewUrl(objectUrl);
      } catch (err) {
        window.alert(err?.message || PDF_PREVIEW_ERROR[kind]);
        if (isMountedRef.current) closePdfPreview();
      } finally {
        if (isMountedRef.current) setPdfPreviewLoading(false);
      }
    })();
  };

  const openStepAttachmentPreview = (doc) => {
    const title = doc?.fileName || `Attachment #${doc?.id || ''}`;
    setPdfPreviewKind('attachment');
    setPdfPreviewTitle(title);
    setPdfPreviewOpen(true);
    setPdfPreviewUrl(null);
    setPdfPreviewLoading(true);
    void (async () => {
      try {
        const raw = await fetchBatchStepDocumentPreviewBase64(batch.id, doc.id);
        const b64 = typeof raw === 'string' ? raw : raw?.data;
        if (!b64 || typeof b64 !== 'string') {
          throw new Error('Empty preview payload');
        }
        const objectUrl = createObjectUrlFromBase64(b64, raw?.fileType || doc?.fileType || 'application/pdf');
        if (!objectUrl) {
          throw new Error('Could not build preview');
        }
        if (pdfPreviewBlobUrlRef.current) {
          URL.revokeObjectURL(pdfPreviewBlobUrlRef.current);
        }
        pdfPreviewBlobUrlRef.current = objectUrl;
        if (isMountedRef.current) setPdfPreviewUrl(objectUrl);
      } catch (err) {
        window.alert(err?.message || 'Could not load attachment preview.');
        if (isMountedRef.current) closePdfPreview();
      } finally {
        if (isMountedRef.current) setPdfPreviewLoading(false);
      }
    })();
  };

  const confirmRemoval = async (note) => {
    if (!removalModal) return;
    setBusy(true);
    try {
      if (removalModal.type === 'withdraw') {
        await withdrawApplicationFromBatch(batch.id, removalModal.applicationId, note);
        notifyApplicationLoanTrackingRefresh({ applicationId: removalModal.applicationId });
      } else {
        await resetLoanProcessingBatch(batch.id, note);
        notifyApplicationLoanTrackingRefresh({
          applicationIds: (batch.members || []).map((m) => m.applicationId),
        });
      }
      await onChanged();
      if (isMountedRef.current) setRemovalModal(null);
    } catch (err) {
      if (isMountedRef.current) {
        window.alert(err?.message || (removalModal.type === 'reset' ? 'Reset Batch failed' : 'Remove failed'));
      }
    } finally {
      if (isMountedRef.current) setBusy(false);
    }
  };

  const removalModalBusy = !!(removalModal && busy);
  const submittedForProcessingLabel = formatSubmittedForProcessing(batch.submittedForProcessingDate);

  return (
    <div
      className={`edo-dashboard-batch-row rounded-0 overflow-hidden ${open ? 'is-open' : ''} ${
        isLastInSection ? '' : 'mb-16'
      }`}
    >
      <button
        type="button"
        className="edo-dashboard-batch-row__toggle w-100 d-flex align-items-center justify-content-between px-16 py-16 bg-white border-0 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-weight-bold text-dark pr-8" style={{ minWidth: 0 }}>
          {batch.name}{' '}
          <span className="text-muted font-weight-normal small">({STATUS_LABELS[batch.status] || batch.status})</span>
          {submittedForProcessingLabel ? (
            <span className="text-muted font-weight-normal small">
              {' '}
              · Date Submitted for Processing: {submittedForProcessingLabel}
            </span>
          ) : null}
        </span>
        <FontAwesomeIcon
          icon={open ? caretDownIcon : caretRightIcon}
          className="text-secondary flex-shrink-0"
          style={{ fontSize: '0.75rem' }}
        />
      </button>
      <Collapse in={open}>
        <div className="px-16 py-16 bg-white">
          <div className="small text-muted mb-12">
            Program: {batch.fundingProgram?.name || batch.fundingProgramId} · {batch.memberCount} loan(s)
            {batch.hasFundingMemo ? ' · Funding Memo on File' : ''}
          </div>
          {showAttachmentsPanel ? (
            <div className="border rounded p-8 mb-12">
              <div className="small text-muted">
                <span className="font-weight-bold">Additional Attachments</span> (Optional)
              </div>
              {showUploadAttachmentButton ? (
                <div className="d-flex flex-wrap align-items-center mt-2 mb-0">
                  <label className="btn btn-sm btn-outline-secondary mr-8 mb-0">
                    {fileUploading
                      ? 'Uploading…'
                      : BATCH_STEP_UPLOAD_LABEL[currentAttachmentStep] || 'Upload Attachment'}
                    <input
                      type="file"
                      className="d-none"
                      onChange={onStepAttachmentFile}
                      disabled={fileUploading || busy}
                    />
                  </label>
                </div>
              ) : null}
              {allStepDocuments.length === 0 ? (
                <div className="small text-muted mt-2 mb-0">No Batch Attachments Uploaded Yet.</div>
              ) : (
                <div className="d-flex flex-column">
                  {allStepDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className="d-flex flex-wrap align-items-center justify-content-between border-top pt-8 mt-8"
                    >
                      <div className="small text-dark mr-8">
                        {doc.fileName || `Attachment #${doc.id}`}
                        <span className="text-muted ml-8">
                          ({BATCH_STEP_DISPLAY_LABEL[doc.stepKey] || doc.stepKey || 'Unknown Step'})
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mr-8"
                          type="button"
                          onClick={() => openStepAttachmentPreview(doc)}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mr-8"
                          type="button"
                          onClick={() => openLoanBatchStepDocumentInNewTab(batch.id, doc.id)}
                        >
                          Download
                        </Button>
                        {currentAttachmentStep && canDeleteCurrentStepAttachment() ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            type="button"
                            disabled={busy}
                            onClick={() => onDeleteStepAttachment(doc.id)}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
          {(batch.members || []).length === 0 ? (
            <p className="text-muted small mb-12 mb-0">No applications in this batch.</p>
          ) : (
            <div className="table-responsive mb-12">
              <Table bordered size="sm" className="mb-0 bg-white">
                <thead className="bg-light">
                  <tr>
                    <th>ID</th>
                    <th>Business Name</th>
                    <th>Program</th>
                    <th>Last Event</th>
                    <th>Last Event Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(batch.members || []).map((m) => (
                    <tr key={m.applicationId}>
                      <td className="align-middle">{m.applicationId}</td>
                      <td className="align-middle">{m.businessName || '—'}</td>
                      <td className="align-middle">
                        {m.programName || batch.fundingProgram?.name || '—'}
                      </td>
                      <td className="align-middle">
                        {formatLastEventType(m.lastEventCode, m.lastEventStatus)}
                      </td>
                      <td className="align-middle">
                        {formatLastEventStatusCol(m.lastEventCode, m.lastEventStatus)}
                      </td>
                      <td className="align-middle text-right text-nowrap">
                        <Link
                          to={`/edo/portfolio/${m.applicationId}?tab=loanTracking`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-dark rounded"
                        >
                          View
                        </Link>
                        {allowMutateBatchMembers && batch.memberCount > 1 ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="ml-8 rounded"
                            disabled={busy}
                            onClick={() =>
                              setRemovalModal({ type: 'withdraw', applicationId: m.applicationId })
                            }
                          >
                            Remove from Batch
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <div className="edo-dashboard-batch-row__actions d-flex flex-wrap align-items-center">
            {batch.status === 'PENDING_L1' && canL1 && (
              <Button
                variant="success"
                size="sm"
                className="mr-8 mb-8"
                disabled={busy}
                onClick={() => run(() => approveBatchL1(batch.id))}
              >
                Approve Batch (L1 → Ex-Director)
              </Button>
            )}
            {batch.status === 'PENDING_L2' && (
              <>
                {canUploadEdoLoanBatchFundingMemo(userData, batch) ? (
                  <label className="btn btn-sm btn-outline-secondary mr-8 mb-8 mb-0">
                    {fileUploading ? 'Uploading…' : 'Upload Funding Memo'}
                    <input type="file" className="d-none" accept=".pdf,application/pdf" onChange={onMemoFile} disabled={fileUploading} />
                  </label>
                ) : null}
                {batch.hasFundingMemo ? (
                  <>
                    <Button variant="outline-secondary" size="sm" className="mr-8 mb-8" onClick={() => openPdfPreview('memo')}>
                      Preview Memo
                    </Button>
                    {canL2Outgoing ? (
                      <Button variant="success" size="sm" className="mr-8 mb-8" disabled={busy} onClick={() => run(() => approveBatchL2(batch.id))}>
                        Approve Batch (L2 → Admin)
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </>
            )}
            {batch.status === 'PENDING_OUTGOING_RECEIPT' && canL2Outgoing && (
              <>
                <Button variant="outline-secondary" size="sm" className="mr-8 mb-8" onClick={() => openPdfPreview('signing')}>
                  Initial / Signing Sheet
                </Button>
                <Button variant="outline-secondary" size="sm" className="mr-8 mb-8" onClick={() => openPdfPreview('initials')}>
                  Combined Loan Tracking (Initials Log)
                </Button>
                <Button variant="primary" size="sm" className="mr-8 mb-8" disabled={busy} onClick={() => run(() => approveBatchOutgoingReceipt(batch.id))}>
                  Record Received from Ex-Director (All)
                </Button>
              </>
            )}
            {batch.status === 'PENDING_TRUSTEE' && canL2Outgoing && (
              <Button variant="primary" size="sm" className="mr-8 mb-8" disabled={busy} onClick={() => run(() => approveBatchTrusteeSend(batch.id))}>
                Send Batch to Trustee (All)
              </Button>
            )}
            {batch.status === 'COMPLETED' && (
              <>
                {batch.hasFundingMemo ? (
                  <Button variant="outline-secondary" size="sm" className="mr-8 mb-8" onClick={() => openPdfPreview('memo')}>
                    Preview Memo
                  </Button>
                ) : null}
                {canL1 ? (
                  <Button variant="outline-secondary" size="sm" className="mr-8 mb-8" onClick={() => openPdfPreview('signing')}>
                    Initial / Signing Sheet
                  </Button>
                ) : null}
                {canL2Outgoing ? (
                  <Button variant="outline-secondary" size="sm" className="mr-8 mb-8" onClick={() => openPdfPreview('initials')}>
                    Combined Loan Tracking (Initials Log)
                  </Button>
                ) : null}
              </>
            )}
            {canRemove && canResetEntireLoanBatch(userData, batch) ? (
              <Button
                variant="outline-danger"
                size="sm"
                className="mr-8 mb-8"
                disabled={busy || !allowMutateBatchMembers}
                title={batch.status === 'COMPLETED' ? 'A completed batch cannot be reset.' : undefined}
                onClick={() => setRemovalModal({ type: 'reset' })}
              >
                Reset Batch (All Applications)
              </Button>
            ) : null}
          </div>
        </div>
      </Collapse>
      <Modal
        show={pdfPreviewOpen}
        onHide={closePdfPreview}
        onExited={handlePdfPreviewExited}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title
            className="text-truncate pr-16 mb-0 flex-grow-1"
            style={{ minWidth: 0 }}
            title={batch.name}
          >
            {pdfPreviewKind
              ? `${PDF_PREVIEW_HEADING[pdfPreviewKind]} — ${
                  pdfPreviewKind === 'attachment' && pdfPreviewTitle ? pdfPreviewTitle : batch.name
                }`
              : ''}
          </Modal.Title>
          <CloseModalButton onCloseModal={closePdfPreview} />
        </Modal.Header>
        <Modal.Body className="p-0 bg-light" style={{ minHeight: '70vh' }}>
          {pdfPreviewLoading ? (
            <Loader className="py-48" message="Loading Preview…" />
          ) : pdfPreviewUrl ? (
            <iframe
              title={
                pdfPreviewKind
                  ? `${PDF_PREVIEW_HEADING[pdfPreviewKind]} Preview — ${
                      pdfPreviewKind === 'attachment' && pdfPreviewTitle
                        ? pdfPreviewTitle
                        : `Batch ${batch.id}`
                    }`
                  : 'PDF preview'
              }
              src={pdfPreviewUrl}
              className="d-block w-100 border-0 bg-white"
              style={{ minHeight: '70vh', height: '70vh' }}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer className="justify-content-end">
          <Button variant="primary" size="sm" type="button" onClick={closePdfPreview}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <LoanBatchRemovalNoteModal
        show={!!removalModal}
        variant={removalModal?.type === 'reset' ? 'resetAll' : 'withdrawOne'}
        onHide={() => {
          if (!removalModalBusy) setRemovalModal(null);
        }}
        onConfirm={confirmRemoval}
        busy={removalModalBusy}
      />
    </div>
  );
}

export default function LoanProcessingBatchesSection({
  title,
  queue,
  status,
  emptyText,
  userData,
  defaultExpanded = true,
  savedExpanded,
  persistExpanded = false,
  preferenceKey,
  preferencesSyncRef,
  headerDragHandle,
  panelAside = null,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listRevision, setListRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const initialExpanded =
    typeof savedExpanded === 'boolean' ? savedExpanded : defaultExpanded;
  const [sectionExpanded, setSectionExpanded] = useState(initialExpanded);

  const sectionDomId = preferenceKey || queue || status || 'batches';
  const collapseBodyId = `loan-processing-batch-${sectionDomId}-body`;
  const collapseHeadingId = `loan-processing-batch-${sectionDomId}-heading`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status ? { status } : { queue };
      const data = await fetchLoanProcessingBatches({
        ...params,
        page,
        pageSize: DASHBOARD_BATCH_PAGE_SIZE,
      });
      const totalCount = typeof data.total === 'number' ? data.total : 0;
      const list = Array.isArray(data.items) ? data.items : [];
      const totalPages = Math.max(1, Math.ceil(totalCount / DASHBOARD_BATCH_PAGE_SIZE));

      if (totalCount > 0 && page > totalPages) {
        setPage(totalPages);
        return;
      }

      setTotal(totalCount);
      setItems(list);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [queue, status, page, listRevision]);

  const notifyQueuesDirty = useCallback(() => {
    notifyLoanProcessingBatchesDashboardDirty();
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onDirty = () => {
      setPage(1);
      setListRevision((x) => x + 1);
    };
    window.addEventListener(LOAN_PROCESSING_BATCHES_DASHBOARD_DIRTY_EVENT, onDirty);
    return () => window.removeEventListener(LOAN_PROCESSING_BATCHES_DASHBOARD_DIRTY_EVENT, onDirty);
  }, []);

  useEffect(() => {
    if (typeof savedExpanded === 'boolean') {
      setSectionExpanded(savedExpanded);
    }
  }, [savedExpanded]);

  const canL1 = useMemo(() => canSeeEdoLoanBatchManagementL1Queue(userData), [userData]);

  const canL2Outgoing = useMemo(() => canSeeEdoLoanBatchManagementL2Queue(userData), [userData]);

  const canRemove = useMemo(() => {
    if (!userData) return false;
    if (isEdoAdminOrOwner(userData)) return true;
    return (userData.edoUserProgramAccesses || []).some((a) =>
      ['ADMIN', 'MANAGER', 'ANALYST', 'STAFF'].includes(a.role)
    );
  }, [userData]);

  const toggleSection = useCallback(async () => {
    const next = !sectionExpanded;
    if (!persistExpanded || !preferenceKey) {
      setSectionExpanded(next);
      return;
    }
    setSectionExpanded(next);
    try {
      const res = await api.patch({
        path: '/users/current/preferences',
        body: {
          edoDashboard: {
            loanProcessingBatchCards: { [preferenceKey]: next },
          },
        },
      });
      const userPreferences = res?.userPreferences;
      if (userPreferences && preferencesSyncRef) {
        preferencesSyncRef.current = userPreferences;
      }
    } catch (err) {
      setSectionExpanded(!next);
      console.error('Failed to save loan processing batch card preference', err);
    }
  }, [sectionExpanded, persistExpanded, preferenceKey, preferencesSyncRef]);

  const onSectionHeaderKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleSection();
      }
    },
    [toggleSection]
  );

  const totalPages = Math.max(1, Math.ceil(total / DASHBOARD_BATCH_PAGE_SIZE));

  return (
    <Card className="edo-dashboard-nested-card border border-gray rounded-0">
      <Card.Header className="edo-dashboard-section-header border-0 rounded-0 px-16 py-12 d-flex align-items-center">
        <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
          {headerDragHandle ? (
            <span
              className="flex-shrink-0 d-inline-flex align-items-center align-self-center"
              onClick={(e) => e.stopPropagation()}
            >
              {headerDragHandle}
            </span>
          ) : null}
          <div
            id={collapseHeadingId}
            role="button"
            tabIndex={0}
            aria-expanded={sectionExpanded}
            aria-controls={collapseBodyId}
            className="flex-grow-1 d-flex align-items-center justify-content-between"
            style={{ minWidth: 0, cursor: 'pointer' }}
            onClick={toggleSection}
            onKeyDown={onSectionHeaderKeyDown}
            title={sectionExpanded ? 'Collapse section' : 'Expand section'}
          >
            <div className="flex-grow-1 pr-8 text-primary mb-0 font-weight-bold" style={{ minWidth: 0 }}>
              {title} ({loading ? '—' : total})
            </div>
            <span className="flex-shrink-0 p-8" aria-hidden="true">
              <FontAwesomeIcon
                icon={sectionExpanded ? caretDownIcon : caretRightIcon}
                className="align-middle edo-dashboard-section-header__chevron"
              />
            </span>
          </div>
        </div>
      </Card.Header>
      <Collapse in={sectionExpanded} appear={false} timeout={SECTION_COLLAPSE_MS}>
        <div id={collapseBodyId}>
          <Card.Body className="px-16 py-16 edo-dashboard-nested-section-body">
            {panelAside ? <div className="mb-12">{panelAside}</div> : null}
            {loading ? (
              <Loader className="py-16" message="Loading Batches…" />
            ) : items.length === 0 ? (
              <p className="text-muted mb-0">{emptyText}</p>
            ) : (
              <>
                {items.map((b, idx) => (
                  <BatchRow
                    key={b.id}
                    batch={b}
                    onChanged={notifyQueuesDirty}
                    canL1={canL1}
                    canL2Outgoing={canL2Outgoing}
                    canRemove={canRemove}
                    userData={userData}
                    defaultOpen={total === 1}
                    isLastInSection={idx === items.length - 1}
                  />
                ))}
                <DashboardTablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </>
            )}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
}
